import { Camera, Bank } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DefinitionBox } from '../../components/onboarding/DefinitionBox';
import { SelectionCountBadge } from '../../components/onboarding/SelectionCountBadge';
import { SelectionGroup } from '../../components/onboarding/SelectionGroup';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { WizardErrorCallout, WizardHelpNote, WizardStepHeader } from '../../components/wizard';
import { useOnboardingFooter } from '../../hooks/useOnboardingFooter';
import { authAPI } from '../../data/auth';
import { accountsAPI, normalizeAccountList } from '../../data/accounts';
import {
  ASSET_GROUPS,
  ONBOARDING_ASSET_PRESELECT,
  ONBOARDING_DEFINITIONS,
} from '../../lib/onboardingCatalogs';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';

function normalizeName(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

export function matchAssetKeysFromExisting(allItems, existingAccounts) {
  const existingNames = new Set(
    (existingAccounts || []).map((item) => normalizeName(item.name)).filter(Boolean),
  );
  return (allItems || [])
    .filter((item) => existingNames.has(normalizeName(item.label)))
    .map((item) => item.key);
}

export function filterNewAssetItems(picked, existingAccounts) {
  const existingNames = new Set(
    (existingAccounts || []).map((item) => normalizeName(item.name)).filter(Boolean),
  );
  return (picked || []).filter((item) => !existingNames.has(normalizeName(item.label)));
}

export function OnboardingAccounts() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const didHydrate = useRef(false);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const allItems = useMemo(
    () => ASSET_GROUPS.flatMap((g) => g.items.map((item) => ({ ...item, tint: g.tint }))),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await accountsAPI.listAccounts();
        if (cancelled) return;
        if (response?.success) {
          setExistingAccounts(normalizeAccountList(response.data));
        }
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loadingAccounts || didHydrate.current || allItems.length === 0) return;
    const matched = matchAssetKeysFromExisting(allItems, existingAccounts);
    if (matched.length > 0) {
      setSelected(matched);
    } else {
      const available = new Set(allItems.map((item) => item.key));
      setSelected(ONBOARDING_ASSET_PRESELECT.filter((key) => available.has(key)));
    }
    didHydrate.current = true;
  }, [allItems, existingAccounts, loadingAccounts]);

  const toggle = (key) => {
    setSelected((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  };

  const advance = useCallback(
    async (skipCreate = false) => {
      setSaving(true);
      setServerError(null);

      if (!skipCreate && selected.length > 0) {
        const picked = allItems.filter((item) => selected.includes(item.key));
        const toCreate = filterNewAssetItems(picked, existingAccounts);
        for (const item of toCreate) {
          const response = await accountsAPI.createAccount({
            name: item.label,
            category: item.category,
            currency: 'EUR',
          });
          if (!response.success) {
            setServerError(
              typeof response.error === 'string'
                ? response.error
                : response.error?.detail || pt.auth.errors.generic,
            );
            setSaving(false);
            return;
          }
        }
      }

      await authAPI.saveOnboardingStep('debts');
      await refreshMe();
      setSaving(false);
      navigate('/onboarding/debts');
    },
    [allItems, existingAccounts, navigate, refreshMe, selected],
  );

  const onContinue = useCallback(() => advance(false), [advance]);
  const onSkip = useCallback(() => advance(true), [advance]);

  useOnboardingFooter({
    primaryLabel: pt.onboarding.common.continue,
    onPrimary: onContinue,
    onSkip,
    skipLabel: pt.onboarding.common.skipAddLater,
    loading: saving,
  });

  if (loadingAccounts) {
    return <SectionSkeleton variant="list" />;
  }

  return (
    <div className="flex flex-col gap-space-6">
      <div className="flex items-start justify-between gap-3">
        <WizardStepHeader
          eyebrow={pt.onboarding.accounts.eyebrow}
          title={pt.onboarding.accounts.title}
          subtitle={pt.onboarding.accounts.subtitle}
          className="flex-1"
        />
        <SelectionCountBadge count={selected.length} className="mt-7 shrink-0" />
      </div>

      <DefinitionBox icon={Bank}>{ONBOARDING_DEFINITIONS.assets}</DefinitionBox>

      {ASSET_GROUPS.map((group) => (
        <SelectionGroup
          key={group.id}
          name={group.name}
          pctLabel={group.pctLabel}
          description={group.description}
          dotClass={group.dotClass}
          tint={group.tint}
          items={group.items}
          selectedKeys={selectedSet}
          getKey={(item) => item.key}
          onToggle={(key) => toggle(key)}
        />
      ))}

      <WizardHelpNote tone="sage" icon={Camera}>
        {pt.onboarding.accounts.selfieNote}
      </WizardHelpNote>

      {serverError ? <WizardErrorCallout>{serverError}</WizardErrorCallout> : null}
    </div>
  );
}
